import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import AccountDialog from '../components/AccountDialog';
import AccountEditDialog from '../components/AccountEditDialog';
import AccountInitializationDialog from '../components/AccountInitializationDialog';
import { bulkDeleteAccounts, initDefaultAccounts } from '../services/api';
import { useAccountStore, useAccountStoreBase } from '../stores/accountStore';
import { toast } from 'react-hot-toast';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, ConfirmModal, BulkSelectCell, ListPagination } from '@redpanda/ui';
import { useBulkActions } from '@redpanda/utils';
import BulkActionButton from '../components/BulkOperations/BulkActionButton';
import BulkSelectHeader from '../components/BulkOperations/BulkSelectHeader';
import { TableSkeleton } from '@redpanda/ui';
import { createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { Role } from '@redpanda/types';

const AccountListPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const userRole = user?.role;

  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isInitDialogOpen, setIsInitDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  
  const {
    accounts,
    pagination,
    isLoading: loading,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    fetchAccounts,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    addAccount,
  } = useAccountStore();

  const canEdit = userRole && [Role.SUPER_ADMIN, Role.ADMIN, Role.USER].includes(userRole);

  // 檢查選中的科目中是否有被使用的
  const hasUsedAccountsSelected = (selectedIds: string[]) => {
    return selectedIds.some(id => {
      const account = accounts?.find(acc => acc.id === id);
      return account?._count?.journalDetails > 0;
    });
  };

  const bulkActions = useBulkActions({
    items: accounts ?? [],
    idField: 'id',
    entityName: t('accounts.item'),
    bulkDeleteApi: bulkDeleteAccounts,
    canDeleteItem: (item: any) => !(item._count && item._count.journalDetails > 0),
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      useAccountStoreBase.setState,
      'account',
      () => accounts,
      () => pagination
    ),
    onSuccess: createBulkDeleteSuccessHandler(
      () => useAccountStoreBase.getState(),
      fetchAccounts,
      setCurrentPage
    ),
    userRole: userRole,
    t,
    toast,
    setCurrentPage
  });

  const {
    selectedItems,
    toggleItemSelection,
    toggleSelectAll,
    showBulkDeleteModal,
    isBulkDeleting,
    handleBulkDelete,
    confirmBulkDelete,
    closeBulkDeleteModal,
    allSelected,
  } = bulkActions;

  const handleEditAccount = (account: any) => {
    setSelectedAccount(account);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedAccount(null);
  };

  const handleDeleteAccount = (account: any) => {
    handleDelete(account.id);
  };

  // 初始化預設會計科目
  const handleInitializeAccounts = async (selectedAccounts: any[]) => {
    setIsInitializing(true);
    try {
      const response = await initDefaultAccounts();

      if (response?.data?.message === 'defaultAccountsExist') {
        toast.success(t('accounts.list.initExist'));
      } else if (response?.data?.message === 'defaultAccountsCreated') {
        toast.success(t('accounts.list.initSuccess', { count: response.data.count || 0 }));
      } else {
        toast.success(t('accounts.list.initComplete'));
      }

      // 重新載入科目列表（強制刷新）
      fetchAccounts(1, true);
      setIsInitDialogOpen(false);
    } catch (error: any) {
      console.error('初始化預設會計科目失敗:', error);

      if (error.response?.data?.message) {
        if (error.response.data.message === 'defaultAccountsExist') {
          toast.success(t('accounts.list.initExist'));
          fetchAccounts(1, true);
          setIsInitDialogOpen(false);
        } else {
          toast.error(error.response.data.message);
        }
      } else {
        toast.error(t('accounts.list.initError'));
      }
    } finally {
      setIsInitializing(false);
    }
  };


  // 在組件載入時獲取資料 (比照記帳分錄頁面)
  useEffect(() => {
    if (isAuthenticated && !isUserLoading) {
      fetchAccounts(1, false); // 使用快取，避免骨架閃爍
    }
  }, [isAuthenticated, isUserLoading, fetchAccounts]);


  // 科目類型顯示名稱
  const getAccountTypeLabel = (type: string) => {
    return t(`accounts.types.${type}`, { defaultValue: type });
  };


  if (isUserLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <div className="p-8 text-center text-red-500">
        {t('accounts.list.unauthorized')}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('accounts.list.title')}</h1>
          <div className="flex space-x-3">
            <BulkActionButton
              selectedCount={selectedItems.length}
              loading={isBulkDeleting}
              onClick={handleBulkDelete}
              disabled={hasUsedAccountsSelected(selectedItems)}
              disabledMessage={t('accounts.list.cannotDeleteUsedBulk')}
            />
            {canEdit && (
              <button
                onClick={() => setIsInitDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
              >
                {t('accounts.list.initializeButton')}
              </button>
            )}
            <button
              onClick={() => setIsAccountDialogOpen(true)}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {t('accounts.list.addButton')}
            </button>
          </div>
        </div>
      </div>


      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={pagination} onPageChange={bulkActions.handlePageChange} isLoading={isPageLoading} className="mb-3" />

      {/* 骨架屏載入狀態 */}
      {(isPageLoading || loading) ? (
        <TableSkeleton rows={10} columns={6} showHeader={true} />
      ) : (
        <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={allSelected}
                    onToggleAll={toggleSelectAll}
                    hasItems={accounts.length > 0}
                  />
                  <TableHead className="w-16">{t('accounts.table.code')}</TableHead>
                  <TableHead className="w-48">{t('accounts.table.name')}</TableHead>
                  <TableHead className="w-20">{t('accounts.table.type')}</TableHead>
                  <TableHead className="w-32">{t('accounts.table.balance')}</TableHead>
                  <TableHead className="w-24">{t('accounts.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-gray-500 dark:text-gray-400">
                      {t('accounts.list.noAccounts')}
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <TableRow key={account.id}>
                      <BulkSelectCell
                        userRole={userRole}
                        isSelected={selectedItems.includes(account.id)}
                        onToggle={() => toggleItemSelection(account.id)}
                        canDelete={!(account._count && account._count.journalDetails > 0)}
                        disabledReason={account._count?.journalDetails > 0 ? t('accounts.list.cannotDeleteUsed') : undefined}
                      />
                      <TableCell className="w-16 font-medium truncate">
                        {account.code}
                      </TableCell>
                      <TableCell className="w-48 truncate">
                        {account.name}
                      </TableCell>
                      <TableCell className="w-20 truncate">
                        {getAccountTypeLabel(account.type)}
                      </TableCell>
                      <TableCell className="w-32 truncate">
                        ${Math.round(Number(account.balance || 0)).toLocaleString()}
                      </TableCell>
                      <TableCell className="w-24">
                        <button
                          onClick={() => handleEditAccount(account)}
                          className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 mr-4"
                        >
                          {t('common.actions.edit')}
                        </button>
                        <button
                          onClick={() => handleDeleteAccount(account)}
                          disabled={account._count?.journalDetails > 0}
                          title={account._count?.journalDetails > 0 ? t('accounts.list.cannotDeleteUsed') : ''}
                          className={`${
                            account._count?.journalDetails > 0
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                          }`}
                        >
                          {t('common.actions.delete')}
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* 分頁控制項 */}
      <ListPagination pagination={pagination} onPageChange={bulkActions.handlePageChange} isLoading={isPageLoading} className="mt-6" />

      {/* 對話框 */}
      <AccountDialog
        isOpen={isAccountDialogOpen}
        onClose={() => setIsAccountDialogOpen(false)}
        onAccountCreated={(newAccount) => {
          addAccount(newAccount); // 直接添加到 store，無骨架閃爍

          // 清除所有年份的報表快取（因為科目總數變更）
          import('../stores/reportStore').then(({ useReportStore }) => {
            const reportStore = useReportStore.getState();
            reportStore.clearAllCache();
          });
        }}
      />
      
      <AccountEditDialog
        isOpen={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        account={selectedAccount}
        onAccountUpdated={() => {
          // 樂觀更新已在 AccountEditDialog 中完成，這裡只做背景刷新
          fetchAccounts(pagination?.currentPage || 1, false);
        }}
      />

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('accounts.list.confirmDeleteTitle')}
        message={t('accounts.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* 批量刪除確認對話框 */}
      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('accounts.list.confirmBulkDeleteTitle')}
        message={t('accounts.list.confirmBulkDeleteMessage', { count: selectedItems.length })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('accounts.list.deleting')}
      />

      {/* 初始化預設會計科目對話框 */}
      <AccountInitializationDialog
        isOpen={isInitDialogOpen}
        onClose={() => setIsInitDialogOpen(false)}
        onInitialize={handleInitializeAccounts}
        isLoading={isInitializing}
      />
    </div>
  );
};

export default AccountListPage;